import { api, Banner, dateTime, Empty, Metric, PageHead, stackOnMobile, useAsync } from "@getaways/extranet-ui";
import { IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useCan } from "../lib/access";
import { filterQuery, FilterBar, useFilters, useOptions } from "../lib/filters";
import { BRANDS, eur, int, pct, STATUS } from "../lib/format";

interface OrderRow {
  orderId: string;
  reference: string;
  brand: string;
  market: string | null;
  origin: string;
  source: string | null;
  status: string;
  bookedAt: string;
  departureDate: string | null;
  nights: number | null;
  agentEmail: string | null;
  agentName: string | null;
  pax: number;
  currency: string;
  sellMinor: number;
  sellEurMinor: number | null;
  marginEurMinor: number | null;
  paidEurMinor: number | null;
  outstandingEurMinor: number | null;
}

interface Totals {
  orders: number;
  pax: number;
  sellEurMinor: number;
  marginEurMinor: number | null;
  paidEurMinor: number | null;
  outstandingEurMinor: number | null;
}

const PAGE = 100;

/** The orders behind any number (docs/36 §7), with the person's scope. */
export function BookingsPage() {
  const can = useCan();
  const money = can("money:read");
  const [filters, setFilters] = useFilters("bookings");
  const options = useOptions();
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const q = filterQuery(filters, { q: search.length >= 2 ? search : undefined });
  const { data, error } = useAsync(() => api<{ rows: OrderRow[]; total: number; totals: Totals }>("GET", `/v1/bookings?${q}&limit=${PAGE}&offset=${offset}`), [q, offset]);
  const t = data?.totals;

  return (
    <>
      <PageHead title="Bookings" sub={data ? `${int(data.total)} order${data.total === 1 ? "" : "s"}` : undefined} />
      {error && <Banner notice={{ tone: "bad", text: error }} />}
      {t && (
        <div className="metrics">
          <Metric label="Orders" value={int(t.orders)} />
          <Metric label="Travellers" value={int(t.pax)} />
          <Metric label="Sold (EUR)" value={eur(t.sellEurMinor)} hint={t.orders ? `${eur(Math.round(t.sellEurMinor / t.orders))} per order` : undefined} />
          {money && <Metric label="Margin" value={eur(t.marginEurMinor)} hint={pct(t.marginEurMinor, t.sellEurMinor)} />}
          <Metric label="Paid" value={eur(t.paidEurMinor)} />
          <Metric label="Still to pay" value={eur(t.outstandingEurMinor)} />
        </div>
      )}
      <div className="card" style={{ padding: 0 }}>
        <FilterBar filters={filters} onChange={(p) => { setFilters(p); setOffset(0); }} options={options} />
        <div className="filters">
          <form style={{ position: "relative", width: 300 }} onSubmit={(e) => { e.preventDefault(); setSearch(text.trim()); setOffset(0); }}>
            <IconSearch size={16} style={{ position: "absolute", left: 10, top: 9, color: "var(--faint)" }} aria-hidden />
            <input value={text} onChange={(e) => setText(e.target.value)} onBlur={() => setSearch(text.trim())} placeholder="Reference, service or agent" style={{ paddingLeft: 32 }} aria-label="Search orders" />
          </form>
        </div>
        {data && data.rows.length === 0 ? <Empty>No orders match.</Empty> : (
          <table ref={stackOnMobile} className="table stack-mobile">
            <thead>
              <tr>
                <th>Reference</th><th>Brand</th><th>Status</th><th>Booked</th><th>Departs</th><th>Agent</th><th className="num">Pax</th><th className="num">Sold</th>
                {money && <><th className="num">Margin</th><th className="num">%</th></>}
                <th className="num">To pay</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows ?? []).map((o) => (
                <tr key={o.orderId}>
                  <td><span className="mono">{o.reference}</span><div className="small muted">{[o.source, o.market, o.origin === "legacy" ? "legacy" : ""].filter(Boolean).join(" · ")}</div></td>
                  <td className="small">{BRANDS[o.brand] ?? o.brand}</td>
                  <td className="small">{STATUS[o.status] ?? o.status}</td>
                  <td className="small">{dateTime(o.bookedAt, "Europe/Lisbon")}</td>
                  <td className="small">{o.departureDate ?? "—"}{o.nights !== null && <div className="muted">{o.nights} nights</div>}</td>
                  <td className="small">{o.agentName ?? o.agentEmail ?? "Site"}</td>
                  <td className="num">{o.pax}</td>
                  <td className="num">{eur(o.sellEurMinor)}{o.currency !== "EUR" && <div className="small muted">{(o.sellMinor / 100).toFixed(0)} {o.currency}</div>}</td>
                  {money && <><td className="num">{eur(o.marginEurMinor)}</td><td className="num">{pct(o.marginEurMinor, o.sellEurMinor)}</td></>}
                  <td className="num">{o.outstandingEurMinor ? eur(o.outstandingEurMinor) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data && data.total > PAGE && (
          <div className="pager">
            <span className="small muted">{offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}</span>
            <span className="spacer" />
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</button>
            <button disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next</button>
          </div>
        )}
      </div>
    </>
  );
}
